import type { JSX } from "solid-js";
import { useLocation } from "@tanstack/solid-router";
import { createQuery } from "@tanstack/solid-query";
import { Show, createEffect, createMemo } from "solid-js";
import { DraftProfileBanner } from "~/components/DraftProfileBanner";
import { TabBar } from "~/components/TabBar";
import { DebLogoMark } from "~/components/DebLogoMark";
import { track } from "~/lib/analytics";
import { everClaimedTicket, fetchProfile } from "~/lib/api";
import { logout, isAuthenticated } from "~/lib/auth";
import { eventIdFromPath, profileQueryKey, showTabBar } from "~/lib/routes";

type Props = {
  children: JSX.Element;
};

export function AppShell(props: Props) {
  const location = useLocation();
  const pathname = () => location().pathname;

  const eventId = createMemo(() => eventIdFromPath(pathname()));
  const tabBarVisible = createMemo(() => showTabBar(pathname()) && isAuthenticated());

  const profileQuery = createQuery(() => ({
    queryKey: profileQueryKey(eventId()),
    queryFn: () => fetchProfile(eventId() ?? undefined),
    enabled: tabBarVisible(),
  }));

  const showDraftBanner = createMemo(
    () =>
      tabBarVisible() &&
      !!eventId() &&
      profileQuery.isSuccess &&
      !everClaimedTicket(profileQuery.data) &&
      !!profileQuery.data?.data?.display_name?.trim(),
  );

  const showBrandHeader = createMemo(() => {
    const path = pathname();
    return !path.startsWith("/auth");
  });

  createEffect(() => {
    const path = pathname();
    if (!isAuthenticated()) return;
    if (path.startsWith("/auth") || path === "/login") return;
    track("page_view", undefined, { page: path, ticketingEventId: eventId() ?? undefined });
  });

  return (
    <div class={`app-shell${tabBarVisible() ? " app-shell--tabbed" : ""}`}>
      <div class="app-frame">
        <Show when={showBrandHeader()}>
          <header class={`app-header app-header--brand${tabBarVisible() ? " app-header--compact" : ""}`}>
            <div class="app-header-brand">
              <DebLogoMark />
              <Show when={!tabBarVisible()}>
                <div class="brand-lockup">
                  <strong>Connect</strong>
                  <span>conference networking</span>
                </div>
              </Show>
            </div>
            {isAuthenticated() && pathname() === "/" && (
              <button type="button" class="btn btn-outline btn-sm" onClick={() => logout()}>
                Sign out
              </button>
            )}
          </header>
        </Show>

        <Show when={showDraftBanner() && eventId()} keyed>
          {(id) => <DraftProfileBanner eventId={id} />}
        </Show>

        <main class={`app-main${tabBarVisible() ? " app-main--tabbed" : ""}`}>{props.children}</main>

        {/* keyed: TanStack Router navigates under startTransition; non-keyed Show
            callback accessors throw "Stale read from <Show>" on first client nav. */}
        <Show when={tabBarVisible() ? (eventId() ?? "all") : null} keyed>
          {(id) => <TabBar eventId={id === "all" ? undefined : id} />}
        </Show>
      </div>
    </div>
  );
}
